<template>
  <div ref="rootRef" class="lookup-select">
    <label v-if="label" class="lookup-label">
      {{ label }}
      <span v-if="required" class="lookup-required">*</span>
    </label>

    <div
      class="lookup-field"
      :class="{
        'lookup-field--open': isOpen,
        'lookup-field--disabled': disabled,
        'lookup-field--error': Boolean(error),
      }"
      @mousedown="onFieldMouseDown"
    >
      <template v-if="multiple">
        <span v-for="item in modelValue" :key="item" class="lookup-chip">
          {{ item }}
          <button
            type="button"
            class="lookup-chip-remove"
            :disabled="disabled"
            tabindex="-1"
            @click.stop="removeItem(item)"
          >
            &times;
          </button>
        </span>
      </template>

      <input
        ref="inputRef"
        v-model="query"
        type="text"
        class="lookup-input"
        :placeholder="inputPlaceholder"
        :disabled="disabled"
        @focus="openDropdown"
        @keydown.enter.prevent="onEnter"
        @keydown.escape.prevent="closeDropdown"
        @keydown.backspace="onBackspace"
      />

      <ChevronDownIcon class="lookup-chevron" aria-hidden="true" />
    </div>

    <div v-if="isOpen" class="lookup-dropdown">
      <p v-if="isLoading" class="lookup-dropdown-state">{{ $t('bo.common.loading') }}</p>
      <p v-else-if="visibleOptions.length === 0" class="lookup-dropdown-state">
        {{ $t('bo.common.noResults') }}
      </p>
      <button
        v-for="option in visibleOptions"
        :key="option.value"
        type="button"
        class="lookup-option"
        :class="{ 'lookup-option--selected': isSelected(option.value) }"
        @mousedown.prevent
        @click="onOptionClick(option.value)"
      >
        <span v-if="multiple" class="lookup-option-check">
          <input type="checkbox" tabindex="-1" :checked="isSelected(option.value)" readonly />
        </span>
        <span class="lookup-option-label">{{ option.label }}</span>
      </button>
    </div>

    <div v-if="showPopularPicks && popularPicks.length" class="lookup-popular">
      <span class="lookup-popular-label">{{ $t('bo.programs.popularPicks') }}</span>
      <div class="lookup-popular-chips">
        <button
          v-for="pick in popularPicks"
          :key="pick.value"
          type="button"
          class="lookup-popular-chip"
          :disabled="disabled || (multiple && isSelected(pick.value))"
          @click="addSelection(pick.value)"
        >
          {{ pick.label }}
          <span class="lookup-popular-plus">+</span>
        </button>
      </div>
    </div>

    <p v-if="error" class="lookup-error">{{ error }}</p>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'
import programLookupService from '@/services/programLookupService'
import {
  addStringSelection,
  mergeLookupOptions,
  normalizeLookupOptions,
  normalizeStringList,
} from '@/utils/programLookups'

const props = defineProps({
  modelValue: { type: [Array, String], default: () => [] },
  kind: {
    type: String,
    required: true,
    validator: (value) => ['industries', 'skills', 'goals'].includes(value),
  },
  label: { type: String, default: '' },
  required: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  error: { type: String, default: '' },
  maxItems: { type: Number, default: null },
  multiple: { type: Boolean, default: true },
  showPopularPicks: { type: Boolean, default: true },
})

const emit = defineEmits(['update:modelValue'])

const rootRef = ref(null)
const inputRef = ref(null)
const query = ref('')
const isOpen = ref(false)
const isLoading = ref(false)
const options = ref([])
let searchTimer = null

const isIndustries = computed(() => props.kind === 'industries')
const isSkills = computed(() => props.kind === 'skills')
const isGoals = computed(() => props.kind === 'goals')

const inputPlaceholder = computed(() => {
  if (props.placeholder) return props.placeholder
  if (props.multiple && props.maxItems && props.modelValue.length >= props.maxItems) {
    return ''
  }
  return props.placeholder
})

const normalizedValue = computed(() => {
  if (props.multiple) {
    return normalizeStringList(Array.isArray(props.modelValue) ? props.modelValue : [], props.maxItems)
  }
  return typeof props.modelValue === 'string' ? props.modelValue : ''
})

const visibleOptions = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return options.value

  return options.value.filter((option) => option.label.toLowerCase().includes(q))
})

const popularPicks = computed(() => {
  if (isGoals.value) {
    return options.value.slice(0, 4)
  }
  return options.value.slice(0, 4)
})

const isSelected = (value) => {
  if (props.multiple) {
    return normalizedValue.value.some((item) => item.toLowerCase() === String(value).toLowerCase())
  }
  return String(normalizedValue.value).toLowerCase() === String(value).toLowerCase()
}

const openDropdown = () => {
  if (props.disabled) return
  isOpen.value = true
}

const closeDropdown = () => {
  isOpen.value = false
}

const onFieldMouseDown = (event) => {
  if (props.disabled) return
  if (event.target === inputRef.value) return
  event.preventDefault()
  inputRef.value?.focus()
  openDropdown()
}

const emitMultiple = (next) => {
  emit('update:modelValue', normalizeStringList(next, props.maxItems))
}

const addSelection = (value) => {
  if (props.disabled) return

  if (props.multiple) {
    if (props.maxItems != null && normalizedValue.value.length >= props.maxItems) return
    emitMultiple(addStringSelection(normalizedValue.value, value, props.maxItems))
    query.value = ''
    return
  }

  emit('update:modelValue', String(value).trim())
  query.value = String(value).trim()
  closeDropdown()
}

const removeItem = (value) => {
  emitMultiple(normalizedValue.value.filter((item) => item !== value))
}

const onOptionClick = (value) => {
  if (props.multiple) {
    if (isSelected(value)) {
      removeItem(value)
      return
    }
    addSelection(value)
    return
  }

  addSelection(value)
}

const onEnter = () => {
  const trimmed = query.value.trim()
  if (!trimmed) return

  if (props.multiple) {
    addSelection(trimmed)
    return
  }

  emit('update:modelValue', trimmed)
  closeDropdown()
}

const onBackspace = () => {
  if (!props.multiple || query.value.trim()) return
  const next = [...normalizedValue.value]
  next.pop()
  emitMultiple(next)
}

const loadIndustries = async () => {
  isLoading.value = true
  try {
    const [groupsRes, sectorsRes] = await Promise.all([
      programLookupService.getSkillGroups({ group_type: 'domain', per_page: 200 }),
      programLookupService.getSectors(),
    ])
    options.value = mergeLookupOptions(
      normalizeLookupOptions(groupsRes?.data),
      normalizeLookupOptions(sectorsRes?.data)
    )
  } catch {
    options.value = []
  } finally {
    isLoading.value = false
  }
}

const loadSkills = async (search = '') => {
  isLoading.value = true
  try {
    const params = search ? { q: search } : { per_page: 250 }
    const res = await programLookupService.getSkills(params)
    options.value = normalizeLookupOptions(res?.data)
  } catch {
    options.value = []
  } finally {
    isLoading.value = false
  }
}

const loadGoals = async () => {
  isLoading.value = true
  try {
    const res = await programLookupService.getGoalCategories()
    options.value = normalizeLookupOptions(res?.data)
  } catch {
    options.value = []
  } finally {
    isLoading.value = false
  }
}

const runSearch = (value) => {
  if (!isSkills.value) return

  if (searchTimer) clearTimeout(searchTimer)

  searchTimer = setTimeout(() => {
    const trimmed = value.trim()
    if (trimmed.length > 0 && trimmed.length < 2) return
    void loadSkills(trimmed)
  }, 250)
}

onClickOutside(rootRef, () => {
  if (props.multiple) {
    closeDropdown()
    return
  }

  const trimmed = query.value.trim()
  if (trimmed && trimmed !== normalizedValue.value) {
    emit('update:modelValue', trimmed)
  }
  closeDropdown()
})

watch(query, (value) => {
  if (!props.multiple && isOpen.value) {
    emit('update:modelValue', value)
  }
  runSearch(value)
})

watch(
  () => props.modelValue,
  (value) => {
    if (props.multiple) {
      const normalized = normalizeStringList(Array.isArray(value) ? value : [], props.maxItems)
      if (JSON.stringify(normalized) !== JSON.stringify(value)) {
        emit('update:modelValue', normalized)
      }
      return
    }

    if (typeof value === 'string' && !isOpen.value) {
      query.value = value
    }
  },
  { immediate: true }
)

onMounted(async () => {
  if (isIndustries.value) {
    await loadIndustries()
    return
  }

  if (isSkills.value) {
    await loadSkills()
    return
  }

  if (isGoals.value) {
    await loadGoals()
    if (!props.multiple && typeof props.modelValue === 'string') {
      query.value = props.modelValue
    }
  }
})

onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer)
})
</script>

<style scoped>
.lookup-select {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  position: relative;
}

.lookup-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.lookup-required {
  color: #dc2626;
}

.lookup-field {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  min-height: 2.5rem;
  padding: 0.375rem 2rem 0.375rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: #fff;
  cursor: text;
  position: relative;
}

.lookup-field--open {
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px #3b82f6;
}

.lookup-field--disabled {
  background: #f9fafb;
  cursor: not-allowed;
  opacity: 0.7;
}

.lookup-field--error {
  border-color: #dc2626;
}

.lookup-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: #f5f0e8;
  color: #44403c;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.8rem;
}

.lookup-chip-remove {
  border: none;
  background: none;
  cursor: pointer;
  color: inherit;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
}

.lookup-input {
  border: none;
  outline: none;
  flex: 1;
  min-width: 120px;
  font-size: 0.875rem;
  padding: 0.25rem;
  background: transparent;
}

.lookup-chevron {
  position: absolute;
  right: 0.625rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  color: #6b7280;
  pointer-events: none;
}

.lookup-dropdown {
  position: absolute;
  z-index: 40;
  left: 0;
  right: 0;
  top: calc(100% + 0.25rem);
  max-height: 16rem;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: #fff;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
}

.lookup-dropdown-state {
  margin: 0;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.lookup-option {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  color: #111827;
  cursor: pointer;
}

.lookup-option:hover,
.lookup-option--selected {
  background: #f3f4f6;
}

.lookup-option-check input {
  pointer-events: none;
}

.lookup-popular {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.lookup-popular-label {
  font-size: 0.75rem;
  color: #9ca3af;
}

.lookup-popular-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.lookup-popular-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border: 1px solid #e7e5e4;
  background: #fff;
  border-radius: 9999px;
  padding: 0.2rem 0.65rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #57534e;
  cursor: pointer;
}

.lookup-popular-chip:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.lookup-popular-plus {
  font-size: 0.875rem;
  line-height: 1;
}

.lookup-error {
  margin: 0;
  font-size: 0.75rem;
  color: #dc2626;
}
</style>
